<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, ref, watch } from "vue";
import { VueDatePicker } from "@vuepic/vue-datepicker";
import type { ModelValue } from "@vuepic/vue-datepicker";
import { enGB, enUS, zhCN, zhTW } from "date-fns/locale";
import { Calendar as CalendarIcon, Clock3 as ClockIcon, X as XIcon } from "lucide-vue-next";
import BaseIcon from "./BaseIcon.vue";
import type {
    BaseDatePickerAdapter,
    BaseDatePickerCalendarCount,
    BaseDatePickerLocale,
    BaseDatePickerMode,
    BaseDatePickerValue,
    DateAdapter,
} from "./base-date-picker-types";
import "@vuepic/vue-datepicker/dist/main.css";

type CompatibleDateAdapter = DateAdapter | BaseDatePickerAdapter;
type TimePickerValue = { hours: number; minutes: number; seconds: number };

const props = withDefaults(defineProps<{
    modelValue: unknown;
    mode?: BaseDatePickerMode;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    loading?: boolean;
    loadingLabel?: string;
    required?: boolean;
    id?: string;
    ariaLabel?: string;
    adapter?: CompatibleDateAdapter;
    locale?: BaseDatePickerLocale;
    /** Calendar accessibility labels forwarded to vue-datepicker. */
    ariaLabels?: Record<string, string>;
    timeZone?: string;
    displayFormat?: string;
    rangeSeparator?: string;
    calendarCount?: BaseDatePickerCalendarCount;
    minDate?: Date | string;
    maxDate?: Date | string;
    autoApply?: boolean;
    enableTimePicker?: boolean;
    theme?: "system" | "light" | "dark";
    /** @deprecated Use theme instead. Kept for existing consumers. */
    dark?: boolean;
    size?: "sm" | "md" | "lg";
    variant?: "default" | "filled" | "outlined" | "plain";
    error?: string | boolean;
}>(), {
    mode: "date",
    disabled: false,
    loading: false,
    loadingLabel: "Loading",
    required: false,
    calendarCount: "single",
    autoApply: true,
    theme: "system",
    dark: undefined,
    size: "md",
    variant: "default",
});

const emit = defineEmits<{
    (event: "update:modelValue", value: unknown): void;
}>();

const globalAdapter = inject<CompatibleDateAdapter | null>("BaseDatePickerAdapter", null);
const mediaQuery = ref<MediaQueryList | null>(null);
const systemDark = ref(false);
const rootDark = ref(false);
const themeObserver = ref<MutationObserver | null>(null);
const isOpen = ref(false);

const effectiveMode = computed<BaseDatePickerMode>(() => (
    props.mode === "date" && props.enableTimePicker ? "datetime" : props.mode
));

const isRangeMode = computed(() => (
    effectiveMode.value === "range"
    || effectiveMode.value === "range-time"
    || effectiveMode.value === "range-datetime"
));

const isTimeOnlyMode = computed(() => (
    effectiveMode.value === "time" || effectiveMode.value === "range-time"
));

const hasDateAndTime = computed(() => (
    effectiveMode.value === "datetime" || effectiveMode.value === "range-datetime"
));

const localeCode = computed(() => (
    typeof props.locale === "string" ? props.locale : undefined
));

const pickerLocale = computed(() => (
    typeof props.locale === "string" ? localeObjectFor(props.locale) : props.locale
));

function localeObjectFor(locale: string) {
    const normalized = locale.toLowerCase().replace("_", "-");
    if (normalized === "en-gb" || normalized === "en-uk") return enGB;
    if (normalized === "en-us" || normalized === "en") return enUS;
    if (normalized === "zh-hant" || normalized === "zh-tw" || normalized === "zh-hk") return zhTW;
    if (normalized === "zh-hans" || normalized === "zh-cn" || normalized === "zh") return zhCN;
    return undefined;
}

const resolvedTimeZone = computed(() => {
    if (props.timeZone) return props.timeZone;
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    } catch {
        return undefined;
    }
});

const rootHasDarkClass = (): boolean => {
    if (typeof document === "undefined") return false;
    return Boolean(document.documentElement.classList.contains("dark")
        || document.body?.closest(".dark"));
};

const resolvedDark = computed(() => {
    if (typeof props.dark === "boolean") return props.dark;
    if (props.theme === "dark") return true;
    if (props.theme === "light") return false;
    return rootDark.value || systemDark.value;
});

const adapterContext = computed(() => ({
    mode: effectiveMode.value,
    timeZone: resolvedTimeZone.value,
    locale: localeCode.value,
}));

const formatParts = (date: Date, timeZone?: string): Record<string, string> => {
    const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
    });
    return formatter.formatToParts(date).reduce<Record<string, string>>((parts, part) => {
        if (part.type !== "literal") parts[part.type] = part.value;
        return parts;
    }, {});
};

const formatPattern = (date: Date, pattern: string, timeZone?: string): string => {
    const parts = formatParts(date, timeZone);
    const values: Record<string, string> = {
        yyyy: parts.year || "",
        yy: (parts.year || "").slice(-2),
        MM: parts.month || "",
        M: String(Number(parts.month || 0)),
        dd: parts.day || "",
        d: String(Number(parts.day || 0)),
        HH: parts.hour || "",
        H: String(Number(parts.hour || 0)),
        mm: parts.minute || "",
        m: String(Number(parts.minute || 0)),
        ss: parts.second || "",
        s: String(Number(parts.second || 0)),
    };
    return pattern.replace(/yyyy|yy|MM|M|dd|d|HH|H|mm|m|ss|s/g, (token) => values[token]);
};

const defaultFormatOne = (date: Date, context: {
    mode: BaseDatePickerMode;
    timeZone?: string;
    locale?: string;
    displayFormat?: string;
}): string => {
    if (context.displayFormat) return formatPattern(date, context.displayFormat, context.timeZone);

    const options: Intl.DateTimeFormatOptions = context.mode === "time" || context.mode === "range-time"
        ? { timeStyle: "short", timeZone: context.timeZone }
        : context.mode === "datetime" || context.mode === "range-datetime"
            ? { dateStyle: "short", timeStyle: "short", timeZone: context.timeZone }
            : { dateStyle: "short", timeZone: context.timeZone };
    return new Intl.DateTimeFormat(context.locale, options).format(date);
};

const defaultAdapter: BaseDatePickerAdapter = {
    parse: (value) => {
        if (value == null || value === "") return null;
        if (Array.isArray(value)) return [parseDate(value[0]), parseDate(value[1])];
        return parseDate(value);
    },
    serialize: (value, context) => {
        if (value == null) return undefined;
        if (Array.isArray(value)) {
            if (!value[0] || !value[1]) return undefined;
            return [
                serializeDefaultDate(value[0], context),
                serializeDefaultDate(value[1], context),
            ];
        }
        return serializeDefaultDate(value, context);
    },
    format: (value, context) => {
        if (value == null) return "";
        if (Array.isArray(value)) {
            return value.map((date) => date ? defaultFormatOne(date, context) : "")
                .join(props.rangeSeparator || " – ");
        }
        return defaultFormatOne(value, context);
    },
};

function parseDate(value: unknown): Date | null {
    if (value == null || value === "") return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split("-").map(Number);
        return new Date(year, month - 1, day);
    }
    const date = new Date(String(value));
    return Number.isNaN(date.getTime()) ? null : date;
}

function serializeDefaultDate(date: Date, context: { mode: BaseDatePickerMode; timeZone?: string }): string {
    if (context.mode === "time" || context.mode === "range-time") {
        return formatPattern(date, "HH:mm", context.timeZone);
    }
    return date.toISOString();
}

const activeAdapter = computed<CompatibleDateAdapter>(() => (
    props.adapter || globalAdapter || defaultAdapter
));

const callParse = (value: unknown): BaseDatePickerValue => {
    try {
        return (activeAdapter.value as BaseDatePickerAdapter).parse(value, adapterContext.value);
    } catch {
        return defaultAdapter.parse(value, adapterContext.value);
    }
};

const callSerialize = (value: BaseDatePickerValue): unknown => {
    if (isRangeMode.value && (!Array.isArray(value) || !value[0] || !value[1])) return undefined;
    try {
        return (activeAdapter.value as BaseDatePickerAdapter).serialize(value, adapterContext.value);
    } catch {
        return defaultAdapter.serialize(value, adapterContext.value);
    }
};

const callFormat = (value: BaseDatePickerValue): string => {
    const context = { ...adapterContext.value, displayFormat: props.displayFormat };
    try {
        return (activeAdapter.value as BaseDatePickerAdapter).format(value, context);
    } catch {
        return defaultAdapter.format(value, context);
    }
};

const normalizeValue = (value: unknown): BaseDatePickerValue => {
    const parsedValue = callParse(value);
    if (isRangeMode.value) {
        if (!Array.isArray(parsedValue)) return [null, null];
        return [parsedValue[0], parsedValue[1]];
    }
    return Array.isArray(parsedValue) ? parsedValue[0] : parsedValue;
};

const pickerValue = ref<BaseDatePickerValue>([null, null]);

const toTimePickerValue = (value: Date | null): TimePickerValue | null => value ? {
    hours: value.getHours(),
    minutes: value.getMinutes(),
    seconds: value.getSeconds(),
} : null;

const fromTimePickerValue = (value: unknown): Date | null => {
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (!value || typeof value !== "object") return null;
    const candidate = value as Partial<TimePickerValue>;
    const hours = Number(candidate.hours);
    const minutes = Number(candidate.minutes);
    const seconds = candidate.seconds === undefined ? 0 : Number(candidate.seconds);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes) || !Number.isInteger(seconds)) return null;
    const date = new Date(1970, 0, 1);
    date.setHours(hours, minutes, seconds, 0);
    return Number.isNaN(date.getTime()) ? null : date;
};

const normalizePickerUpdateValue = (value: unknown): BaseDatePickerValue => {
    if (!isTimeOnlyMode.value) {
        if (isRangeMode.value) {
            if (!Array.isArray(value)) return [null, null];
            return [parseDate(value[0]), parseDate(value[1])];
        }
        return parseDate(value);
    }
    if (isRangeMode.value) {
        if (!Array.isArray(value)) return [null, null];
        return [fromTimePickerValue(value[0]), fromTimePickerValue(value[1])];
    }
    return fromTimePickerValue(value);
};

const pickerModelValue = computed<ModelValue>(() => {
    if (isTimeOnlyMode.value) {
        if (isRangeMode.value) {
            if (!Array.isArray(pickerValue.value)) return null;
            return [
                toTimePickerValue(pickerValue.value[0]),
                toTimePickerValue(pickerValue.value[1]),
            ] as ModelValue;
        }
        return toTimePickerValue(pickerValue.value as Date | null);
    }
    if (!isRangeMode.value) return pickerValue.value as Date | null;
    if (!Array.isArray(pickerValue.value)) return null;
    if (pickerValue.value[0] && pickerValue.value[1]) return [pickerValue.value[0], pickerValue.value[1]];
    if (pickerValue.value[0]) return [pickerValue.value[0]];
    return null;
});

const syncFromModel = () => {
    pickerValue.value = normalizeValue(props.modelValue);
};

watch(
    [() => props.modelValue, effectiveMode, activeAdapter, resolvedTimeZone, localeCode],
    syncFromModel,
    { immediate: true, deep: true },
);

const onPickerUpdate = (value: unknown) => {
    // Picker updates are already normalized to the shared internal Date
    // contract. Do not send them through the consumer parser again: adapters
    // commonly parse their serialized value (for example, POS "HH:mm") and
    // intentionally do not accept the third-party Date model.
    const normalized = normalizePickerUpdateValue(value);
    pickerValue.value = normalized;
    if (isRangeMode.value && (!Array.isArray(normalized) || !normalized[0] || !normalized[1])) return;
    emit("update:modelValue", callSerialize(normalized));
};

const clearValue = () => {
    pickerValue.value = isRangeMode.value ? [null, null] : null;
    emit("update:modelValue", undefined);
};

const displayValue = computed(() => callFormat(pickerValue.value));
const hasValue = computed(() => isRangeMode.value
    ? Array.isArray(pickerValue.value) && Boolean(pickerValue.value[0] || pickerValue.value[1])
    : Boolean(pickerValue.value));

const pickerFormat = computed(() => props.displayFormat || undefined);
const placeholderText = computed(() => {
    if (props.placeholder) return props.placeholder;
    if (props.displayFormat) return props.displayFormat;
    if (effectiveMode.value === "time" || effectiveMode.value === "range-time") return "HH:mm";
    return effectiveMode.value === "range" || effectiveMode.value === "range-datetime"
        ? "Select date range"
        : "Select date";
});

const pickerTimeConfig = computed(() => ({
    enableTimePicker: hasDateAndTime.value,
    is24: true,
    enableSeconds: false,
}));

const pickerRange = computed(() => isRangeMode.value ? {
    partialRange: false,
    autoSwitchStartEnd: true,
} : false);

const pickerCalendars = computed(() => (
    isRangeMode.value && !isTimeOnlyMode.value && props.calendarCount === "dual" ? 2 : false
));

const sizeClasses = computed(() => {
    switch (props.size) {
        case "sm": return "base-date-picker__trigger--sm";
        case "lg": return "base-date-picker__trigger--lg";
        default: return "base-date-picker__trigger--md";
    }
});

const triggerClasses = computed(() => [
    "base-date-picker__trigger",
    `base-date-picker__trigger--${props.variant}`,
    sizeClasses.value,
    resolvedDark.value ? "base-date-picker__trigger--dark" : "",
    props.disabled || props.loading ? "base-date-picker__trigger--disabled" : "",
    hasError.value ? "base-date-picker__trigger--error" : "",
    isOpen.value ? "base-date-picker__trigger--open" : "",
]);

const hasError = computed(() => (
    props.error === true || (typeof props.error === "string" && props.error.trim() !== "")
));
const errorMessage = computed(() => typeof props.error === "string" ? props.error : "");
const triggerIcon = computed(() => isTimeOnlyMode.value ? ClockIcon : CalendarIcon);

const onMediaChange = () => {
    systemDark.value = Boolean(mediaQuery.value?.matches);
};

onMounted(() => {
    mediaQuery.value = window.matchMedia("(prefers-color-scheme: dark)");
    onMediaChange();
    rootDark.value = rootHasDarkClass();
    themeObserver.value = new MutationObserver(() => {
        rootDark.value = rootHasDarkClass();
    });
    themeObserver.value.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    mediaQuery.value.addEventListener?.("change", onMediaChange);
});

onUnmounted(() => {
    mediaQuery.value?.removeEventListener?.("change", onMediaChange);
    themeObserver.value?.disconnect();
});
</script>

<template>
    <div :class="['base-date-picker', { 'base-date-picker--dark': resolvedDark }]">
        <label v-if="label" :for="id" class="base-date-picker__label">
            {{ label }}
            <span v-if="required" class="base-date-picker__required">*</span>
        </label>

        <VueDatePicker
            :model-value="pickerModelValue"
            :disabled="disabled || loading"
            :dark="resolvedDark"
            :time-picker="isTimeOnlyMode"
            :time-config="pickerTimeConfig"
            :range="pickerRange"
            :multi-calendars="pickerCalendars"
            :timezone="timeZone || undefined"
            :locale="pickerLocale"
            :aria-labels="ariaLabels"
            :min-date="minDate"
            :max-date="maxDate"
            :format="pickerFormat"
            :range-separator="rangeSeparator || ' – '"
            :auto-apply="autoApply"
            hide-action-row
            teleport="body"
            @update:model-value="onPickerUpdate"
            @open="isOpen = true"
            @closed="isOpen = false"
        >
            <template #trigger>
                <div :class="triggerClasses">
                    <div class="base-date-picker__icon" :aria-label="loading ? loadingLabel : undefined">
                        <BaseIcon v-if="loading" name="Loader2" class="h-4 w-4 animate-spin" decorative />
                        <component v-else :is="triggerIcon" class="h-4 w-4" />
                    </div>

                    <div class="base-date-picker__value">
                        <input
                            :id="id"
                            :value="displayValue"
                            :placeholder="placeholderText"
                            :disabled="disabled || loading"
                            :aria-label="ariaLabel"
                            :aria-busy="loading || undefined"
                            :aria-invalid="hasError || undefined"
                            readonly
                            class="base-date-picker__input"
                        />

                        <button
                            v-if="hasValue && !disabled && !loading"
                            type="button"
                            class="base-date-picker__clear"
                            aria-label="Clear date"
                            @click.stop="clearValue"
                        >
                            <XIcon class="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>
            </template>
        </VueDatePicker>

        <p v-if="hasError && errorMessage" class="base-date-picker__error">
            <BaseIcon name="AlertCircle" :size="11" />
            {{ errorMessage }}
        </p>
    </div>
</template>

<style>
:root {
    --dp-font-size: 0.75rem;
    --dp-border-radius: 0.5rem;
    --dp-menu-z-index: 9999;
}

.base-date-picker {
    display: flex;
    width: 100%;
    flex-direction: column;
    gap: 0.25rem;
    text-align: left;
}

.base-date-picker__label {
    display: flex;
    gap: 0.125rem;
    color: var(--color-text-muted, #6b7280);
    font-size: 0.75rem;
    font-weight: 700;
}

.base-date-picker__required,
.base-date-picker__error {
    color: var(--color-danger, #ef4444);
}

.base-date-picker__trigger {
    display: flex;
    min-width: 0;
    align-items: stretch;
    overflow: hidden;
    cursor: pointer;
    border: 1px solid var(--color-border, #e5e7eb);
    border-radius: 0.5rem;
    background: var(--color-surface-elevated, #ffffff);
    color: var(--color-text, #374151);
    transition: border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;
}

.base-date-picker__trigger--sm { height: 1.875rem; }
.base-date-picker__trigger--md { height: 2.5rem; }
.base-date-picker__trigger--lg { height: 3rem; font-size: 1rem; }
.base-date-picker__trigger--filled { background: var(--color-surface-muted, #f3f4f6); border-color: transparent; }
.base-date-picker__trigger--outlined { border-width: 2px; background: transparent; }
.base-date-picker__trigger--plain { border: 0; background: transparent; }
.base-date-picker__trigger:hover,
.base-date-picker__trigger--open { border-color: var(--color-primary, #0e8aa0); box-shadow: 0 0 0 2px color-mix(in srgb, var(--color-primary, #0e8aa0) 16%, transparent); }
.base-date-picker__trigger--disabled { cursor: not-allowed; background: var(--color-surface-muted, #f3f4f6); color: var(--color-text-dim, #9ca3af); }
.base-date-picker__trigger--error { border-color: var(--color-danger, #fca5a5); box-shadow: 0 0 0 2px color-mix(in srgb, var(--color-danger, #ef4444) 12%, transparent); }
.base-date-picker__trigger--dark { border-color: var(--color-border, #1f2937); background: var(--color-surface, #111827); color: var(--color-text, #e5e7eb); }
.base-date-picker__trigger--dark.base-date-picker__trigger--filled { background: var(--color-surface-elevated, #1f2937); }
.base-date-picker__trigger--dark.base-date-picker__trigger--disabled { background: var(--color-surface-elevated, #1f2937); color: var(--color-text-dim, #6b7280); }
.base-date-picker__trigger--dark.base-date-picker__trigger--error { border-color: var(--color-danger, #991b1b); }

.base-date-picker__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.25rem 0 0.625rem;
    color: var(--color-text-dim, #9ca3af);
}

.base-date-picker__value {
    position: relative;
    display: flex;
    min-width: 0;
    flex: 1 1 auto;
    align-items: center;
}

.base-date-picker__input {
    height: 100%;
    width: 100%;
    min-width: 0;
    cursor: pointer;
    border: 0;
    background: transparent;
    padding: 0 0.375rem;
    color: inherit;
    font: inherit;
    font-size: 0.75rem;
    outline: none;
}

.base-date-picker__input::placeholder { color: var(--color-text-dim, #9ca3af); }
.base-date-picker--dark .base-date-picker__input::placeholder { color: var(--color-text-dim, #6b7280); }

.base-date-picker__clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-inline-end: 0.25rem;
    border: 0;
    background: transparent;
    color: var(--color-text-dim, #9ca3af);
    cursor: pointer;
}

.base-date-picker__clear:hover { color: var(--color-danger, #ef4444); }

.base-date-picker__error {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin: 0.25rem 0 0;
    font-size: 0.6875rem;
    font-weight: 600;
}

.dp__main { font-family: inherit; }
.dp__menu {
    border: 1px solid var(--dp-menu-border-color, var(--color-border, #e5e7eb)) !important;
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 10%), 0 4px 6px -2px rgb(0 0 0 / 5%) !important;
}

.dp__theme_light {
    --dp-background-color: var(--color-surface-elevated, #ffffff);
    --dp-text-color: var(--color-text, #1f2937);
    --dp-hover-color: var(--color-surface-muted, #f3f4f6);
    --dp-hover-text-color: var(--color-text, #1f2937);
    --dp-hover-icon-color: var(--color-text-muted, #6b7280);
    --dp-primary-color: var(--color-primary, #0e8aa0);
    --dp-primary-disabled-color: var(--color-primary-light, #8ccbd4);
    --dp-primary-text-color: var(--color-surface, #ffffff);
    --dp-secondary-color: var(--color-text-dim, #9ca3af);
    --dp-border-color: var(--color-border, #e5e7eb);
    --dp-menu-border-color: var(--color-border, #e5e7eb);
    --dp-border-color-hover: var(--color-primary, #0e8aa0);
    --dp-disabled-color: var(--color-surface-muted, #f3f4f6);
    --dp-scroll-bar-background: var(--color-surface-muted, #f3f4f6);
    --dp-scroll-bar-color: var(--color-text-dim, #9ca3af);
    --dp-icon-color: var(--color-text-muted, #6b7280);
    --dp-danger-color: var(--color-danger, #ef4444);
    --dp-marker-color: var(--color-danger, #ef4444);
    --dp-tooltip-color: var(--color-surface-elevated, #ffffff);
    --dp-disabled-color-text: var(--color-text-dim, #9ca3af);
    --dp-highlight-color: color-mix(in srgb, var(--color-primary, #0e8aa0) 12%, transparent);
    --dp-range-between-dates-background-color: color-mix(in srgb, var(--color-primary, #0e8aa0) 15%, transparent);
    --dp-range-between-dates-text-color: var(--color-text, #1f2937);
    --dp-range-between-border-color: transparent;
}

.dp__theme_dark {
    --dp-background-color: var(--color-surface, #111827);
    --dp-text-color: var(--color-text, #f3f4f6);
    --dp-hover-color: var(--color-surface-elevated, #374151);
    --dp-hover-text-color: var(--color-text, #ffffff);
    --dp-hover-icon-color: var(--color-text-muted, #d1d5db);
    --dp-primary-color: var(--color-primary, #20a0b5);
    --dp-primary-disabled-color: var(--color-primary-hover, #256b76);
    --dp-primary-text-color: var(--color-surface-inverse, #ffffff);
    --dp-secondary-color: var(--color-text-dim, #9ca3af);
    --dp-border-color: var(--color-border-strong, #374151);
    --dp-menu-border-color: var(--color-border-strong, #374151);
    --dp-border-color-hover: var(--color-primary, #20a0b5);
    --dp-disabled-color: var(--color-surface-elevated, #1f2937);
    --dp-scroll-bar-background: var(--color-surface-elevated, #1f2937);
    --dp-scroll-bar-color: var(--color-text-dim, #4b5563);
    --dp-icon-color: var(--color-text-muted, #9ca3af);
    --dp-danger-color: var(--color-danger, #f87171);
    --dp-marker-color: var(--color-danger, #f87171);
    --dp-tooltip-color: var(--color-surface, #111827);
    --dp-disabled-color-text: var(--color-text-dim, #6b7280);
    --dp-highlight-color: color-mix(in srgb, var(--color-primary, #20a0b5) 20%, transparent);
    --dp-range-between-dates-background-color: color-mix(in srgb, var(--color-primary, #20a0b5) 22%, transparent);
    --dp-range-between-dates-text-color: var(--color-text, #f3f4f6);
    --dp-range-between-border-color: transparent;
}

.dp__menu.dp__theme_dark {
    border-color: var(--dp-menu-border-color, var(--color-border-strong, #374151)) !important;
    box-shadow: 0 20px 25px -5px rgb(0 0 0 / 30%), 0 10px 10px -5px rgb(0 0 0 / 20%) !important;
}

@media (max-width: 640px) {
    .dp__menu { max-width: calc(100vw - 1rem); }
}
</style>
